/**
 * Every Float on the program-stats payload is nullable and null means
 * "unmeasured", not zero — most production encounters have no turnaround start
 * at all. Rendering a null as 0% would report a real, terrible number for
 * something nobody measured, so every formatter here maps null to an em dash.
 */
export const UNMEASURED = "—";

/**
 * Shown against a program that has configured no turnaround target. It is a
 * placeholder for the reader, never an input: the backend substitutes no
 * default either, so every within-target figure arrives null alongside it and
 * nothing is computed from this number.
 */
export const DEFAULT_TURNAROUND_TARGET_MINUTES = 30;

export const formatPct = (value: number | null | undefined): string =>
  value === null || value === undefined ? UNMEASURED : `${Math.round(value)}%`;

export const formatMinutes = (value: number | null | undefined): string =>
  value === null || value === undefined
    ? UNMEASURED
    : `${Math.round(value)} min`;

export const formatTurnaroundTarget = (
  targetMinutes: number | null | undefined,
): string =>
  targetMinutes === null || targetMinutes === undefined
    ? `turnaround ${DEFAULT_TURNAROUND_TARGET_MINUTES} min — not set`
    : `turnaround ${targetMinutes} min`;

export const formatDelta = (deltaPct: number | null | undefined): string =>
  deltaPct === null || deltaPct === undefined
    ? "vs prior period"
    : `${deltaPct >= 0 ? "▲" : "▼"} ${deltaPct > 0 ? "+" : ""}${Math.round(deltaPct)}% vs prior`;

/**
 * A principal with no `EpocusUser` row comes back with a null name. Falling
 * back to the id would put a UUID in a clinician-facing table; inventing a name
 * would attribute reviews to someone who does not exist.
 */
export const displayName = (
  name: string | null | undefined,
  kind: "reviewer" | "performer",
): string => name ?? `Unnamed ${kind}`;

/**
 * Bar widths are a share of a whole, so a null share has no width to draw —
 * distinct from a real 0%, which draws an empty bar.
 */
export const barWidthPct = (value: number | null | undefined): number | null =>
  value === null || value === undefined
    ? null
    : Math.max(0, Math.min(100, value));
